import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  Box,
  Button,
  Input as DollarInput,
  InputAdornment,
  Tab,
  Tabs,
  styled,
} from "@mui/material";
import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { CreatePaymentMethodCardData } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import SuccessModal, {
  useSuccess,
} from "../../../../../components/SuccessModal";
import Text from "../../../../../components/Text";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import CustomCardElement from "../../../../../components/app/provider/billing/payment/CustomCardElement";
import { PaymentBankFields } from "../../../../../components/app/provider/billing/payment/PaymentBankFields";
import { PaymentCashFields } from "../../../../../components/app/provider/billing/payment/PaymentCashFields";
import { SavedCards } from "../../../../../components/app/provider/billing/payment/SavedCards";
import {
  CARD_HOLDER_NAME_SCHEMA,
  PAYMENT_DATE_REQUIRED_MSG,
  PAYMENT_MODE,
  PAYMENT_OPTIONS,
} from "../../../../../components/app/provider/billing/payment/payment-constant";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetPatientCards } from "../../../../../queries/billings/payments/cards/use-patient-cards";
import { usePatientAttachPaymentMethod } from "../../../../../queries/stripe/use-attach-payment-card";
import { useStripeCharge } from "../../../../../queries/stripe/use-stripe";
import {
  PaymentRequestPaymentCategoryEnum,
  PaymentRequestPaymentModeEnum,
} from "../../../../../services";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";

const StyledTab = styled(Tab)(() => ({
  alignItems: "flex-start",
  textTransform: "none",
}));

interface PaymentDrawerProps {
  disclosureHost: DisclosureHost;
  amount?: string;
  shouldPassPatientId?: boolean;
  selectedMode?: PaymentRequestPaymentModeEnum;
  setSelectedPaymentMethod?: (mode: PaymentRequestPaymentModeEnum) => void;
  onSuccess?: () => void;
  providerId?: { uuid: string; name: string };
  paymentCategory?: PaymentRequestPaymentCategoryEnum;
}

const PaymentDrawer = ({
  disclosureHost,
  amount,
  shouldPassPatientId,
  selectedMode,
  setSelectedPaymentMethod,
  onSuccess,
  providerId,
  paymentCategory,
}: PaymentDrawerProps) => {
  const effectiveProviderId = providerId ?? disclosureHost?.data?.providerId;
  const providerIdMap = effectiveProviderId?.uuid
    ? { [effectiveProviderId.uuid]: effectiveProviderId.name ?? "" }
    : undefined;

  const [activeTab, setActiveTab] = useState<PaymentRequestPaymentModeEnum>(
    selectedMode ?? (PAYMENT_OPTIONS[0].value as PaymentRequestPaymentModeEnum)
  );

  const [showCardUi, setShowCardUi] = useState<boolean>(false);
  const { data: cards, refetch: refetchCards } = useGetPatientCards({
    pagination: DEF_PAGINATION,
    typeOfPayment: "PATIENT",
    paymentMode: "ONLINE",
    cardStatus: "ACTIVE",
    patientUuid: disclosureHost?.data?.patientId,
    enabled: disclosureHost.opened,
  });
  const [
    successPayment,
    { open: openSuccessPayment, close: closeSuccessPayment },
  ] = useSuccess(onSuccess);
  const [selectedCard, setSelectedCard] = useState<any>();
  const { mutateAsync: attachPayment } = usePatientAttachPaymentMethod();
  const stripe = useStripe();
  const elements = useElements();
  const { mutateAsync: chargeStripe } = useStripeCharge();
  const showSnackbar = useSnackbar();
  // The Date field defaults to today. `startOf("day")` is what the date picker
  // itself produces on selection, and it keeps this object deep-equal on every
  // render so `enableReinitialize` never resets the form mid-entry.
  const initialValues = {
    amount: undefined,
    note: "",
    date: undefined,
    paymentDate: dayjs().startOf("day"),
    cardHolderName: "",
  };

  // Cash, bank and saved-card payments never show the card holder field, so it
  // is only required while a new card is being entered.
  const isAddingNewCard = activeTab === PAYMENT_MODE.ONLINE && showCardUi;

  // The Date field is only rendered for cash payments, where it is mandatory.
  const isPaymentDateShown = activeTab === PAYMENT_MODE.CASH;

  const validationSchema = Yup.object({
    amount: Yup.number()
      .typeError("Amount must be a valid number")
      .moreThan(0, "Amount must be greater than 0")
      .required()
      .label("Amount"),
    ...(isAddingNewCard ? { cardHolderName: CARD_HOLDER_NAME_SCHEMA } : {}),
    ...(isPaymentDateShown
      ? {
          paymentDate: Yup.mixed()
            .nullable()
            .required(PAYMENT_DATE_REQUIRED_MSG),
        }
      : {}),
  });

  const closeAndReset = () => {
    formik.resetForm();
    setActiveTab("CASH");
    disclosureHost.close();
  };

  const checkForPatientId = () => {
    if (!disclosureHost.data?.patientId) {
      showSnackbar({
        type: "error",
        message: "Patient is required",
      });
    }
  };

  const handleSaveCard = async (values: any) => {
    const cardId =
      selectedCard && activeTab === "ONLINE"
        ? selectedCard?.paymentMethodId
        : undefined;

    const paymentRequest = {
      ...values,
      cardId,
      paymentCategory,
      superBill: disclosureHost?.data?.superBillId,
      patient: shouldPassPatientId
        ? { [disclosureHost?.data?.patientId]: "" }
        : undefined,
      visitId: !shouldPassPatientId ? disclosureHost?.data?.uuid : undefined,
      bankPaymentMode:
        activeTab === "BANK" ? values?.bankPaymentMode?.value : undefined,
      amount: values?.amount,
      currency: "usd",
      paymentMode: activeTab,
      providerId: providerIdMap,
      subTotal: disclosureHost?.data?.subTotal ?? 0,
    };

    if (stripe && elements && activeTab === "ONLINE" && showCardUi) {
      const cardNumberElement = elements?.getElement(CardNumberElement);
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: { name: formik.values?.cardHolderName?.trim() },
      } as CreatePaymentMethodCardData);

      //   check for patient id if not then return
      checkForPatientId();

      if (!error) {
        attachPayment({
          patientUuid: disclosureHost?.data?.patientId,
          paymentMethodId: paymentMethod.id,
          xTENANTID: getXtenantId(),
        })
          .then(() => {
            refetchCards();

            const paymentRequest = {
              paymentCategory: paymentCategory,
              superBill: disclosureHost?.data?.superBillId,
              patient: shouldPassPatientId
                ? { [disclosureHost?.data?.patientId]: "" }
                : undefined,
              visitId: !shouldPassPatientId
                ? disclosureHost?.data?.uuid
                : undefined,
              cardId: paymentMethod.id,
              amount: values?.amount,
              currency: "usd",
              paymentMode: activeTab,
              providerId: providerIdMap,
              subTotal: disclosureHost?.data?.subTotal ?? 0,
            };
            showToastOnApiCall({
              apiCall: chargeStripe({
                paymentRequest: paymentRequest,
                xTENANTID: getXtenantId(),
              }),
              showSnackbar,
              onSave: openSuccessPayment,
              onDelay: () => setTimeout(closeAndReset, 1400),
            });
          })
          .catch((err) => showSnackbar(getErrorSnackbar(err)));
      } else {
        showSnackbar({
          type: "error",
          message: error?.message ?? "SomeThing Went Wrong",
        });
      }
    } else {
      showToastOnApiCall({
        apiCall: chargeStripe({
          paymentRequest: paymentRequest,
          xTENANTID: getXtenantId(),
        }),
        showSnackbar,
        onSave: openSuccessPayment,
        onDelay: () => setTimeout(closeAndReset, 1400),
      });
    }
  };

  const formik = useFormik({
    initialValues: amount ? { ...initialValues, amount } : initialValues,
    validationSchema,
    onSubmit: handleSaveCard,
    enableReinitialize: true,
  });

  const handleTabChange = (ind: number) => {
    const mode = PAYMENT_OPTIONS[ind].value as PaymentRequestPaymentModeEnum;

    setActiveTab(mode);
    setSelectedPaymentMethod?.(mode);
    setShowCardUi(false);
  };

  // Auto-select the patient's primary card when cards are available, falling
  // back to the first card if none is marked primary.
  useEffect(() => {
    if (
      cards?.data?.content?.length &&
      activeTab === "ONLINE" &&
      !showCardUi &&
      !selectedCard
    ) {
      const cardList = cards.data.content;
      const primaryCard = cardList.find((card) => card?.primary) ?? cardList[0];
      setSelectedCard({ ...primaryCard });
    }
  }, [cards?.data?.content, activeTab, showCardUi, selectedCard]);
  return (
    <SideSheetDrawer
      w="840px"
      onClose={disclosureHost.close}
      open={disclosureHost.opened}
      title="Payment"
      footerActions={
        <>
          <Button variant="outlined" onClick={disclosureHost.close}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={formik.submitForm}
            sx={{ width: "120px" }}
          >
            Pay
          </Button>
        </>
      }
    >
      <Flex gap={2} height={"calc(100vh - 188px)"}>
        <SuccessModal
          label="Payment Successfully Added"
          opened={successPayment}
          onClose={closeSuccessPayment}
        />

        <Box
          sx={{
            width: "30%",
            borderRight: "solid 1px black",
          }}
        >
          <Tabs
            sx={{ ".MuiTab-root": { paddingLeft: "0px !important" } }}
            orientation="vertical"
            value={PAYMENT_OPTIONS.findIndex((it) => it.value === activeTab)}
            indicatorColor={"white" as any}
            onChange={(_, ind) => handleTabChange(ind)}
          >
            {PAYMENT_OPTIONS.map((tab) => {
              return (
                <StyledTab
                  key={tab.value}
                  label={
                    <Flex height={"100%"} gap={2} mb={1}>
                      <Box
                        sx={{
                          width: "5px",
                          height: "30px",
                          bgcolor:
                            tab.value === activeTab
                              ? "primary.main"
                              : undefined,
                          aspectRatio: 2 / 3,
                          clipPath: "ellipse(100% 85% at 0 50%)",
                        }}
                      />
                      <Flex
                        justifyContent={"left"}
                        alignItems="center"
                        gap={1}
                        width={"100%"}
                      >
                        {tab.icon}
                        <Text
                          fontWeight={
                            tab.value === activeTab ? "bold" : undefined
                          }
                        >
                          {tab.label}
                        </Text>
                      </Flex>
                    </Flex>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
        <Flex gap={2} flexDirection={"column"} width={"100%"}>
          <Box
            sx={{
              height: "109px",
              width: "300px",
            }}
          >
            <ErrorHelper error={formik.touched.amount && formik.errors?.amount}>
              <Flex
                justifyContent={"space-between"}
                flexDirection={"column"}
                borderRadius={3}
                border={`2px solid ${
                  formik.touched.amount && formik.errors?.amount
                    ? "#dd5d5d"
                    : "#004186"
                }`}
                height={"100%"}
                width={"100%"}
                p={2}
              >
                <Text fontSize={"20px"}>Payment Amount</Text>
                <Box
                  sx={{
                    width: "55%",
                  }}
                >
                  <DollarInput
                    type="number"
                    value={formik.values.amount}
                    name="amount"
                    inputProps={{ min: 0 }}
                    onKeyDown={(e) => {
                      if (["-", "+", "e", "E"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={formik.handleChange}
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                  />
                </Box>
              </Flex>
            </ErrorHelper>
          </Box>
          {isPaymentDateShown && <PaymentCashFields formik={formik} />}

          {/* card  */}
          {activeTab === "ONLINE" && (
            <>
              {!!cards?.data?.content?.length && (
                <Flex
                  gap={1.8}
                  width={"100%"}
                  mt={2}
                  sx={{ overflowY: "scroll" }}
                  flexWrap={"wrap"}
                  height={"20rem"}
                  maxHeight={"30rem"}
                >
                  <SavedCards
                    cards={cards?.data?.content as any}
                    selectedCard={selectedCard}
                    onSelect={(val) => {
                      if (!showCardUi) setSelectedCard(val);
                    }}
                  />
                </Flex>
              )}
              {!showCardUi ? (
                <Box>
                  <StyledButtonV2
                    sIcon={<AddCircleOutlineIcon />}
                    variant="text"
                    label="Add New Card"
                    onClick={() => {
                      setShowCardUi((prev) => !prev);
                      setSelectedCard(undefined);
                    }}
                  />
                </Box>
              ) : (
                <Flex flexDirection={"column"}>
                  <Flex width={"100%"} justifyContent={"end"} mb={2}>
                    <Box>
                      <StyledButtonV2
                        sIcon={<CancelOutlinedIcon />}
                        variant="text"
                        label="Remove New Card"
                        onClick={() => setShowCardUi((prev) => !prev)}
                      />
                    </Box>
                  </Flex>

                  {/* <CardElement /> */}
                  <CustomCardElement
                    cardHolderName={formik.values.cardHolderName}
                    cardHolderError={formik.errors.cardHolderName}
                    onCardHolderChange={(name) =>
                      formik.setFieldValue("cardHolderName", name)
                    }
                  />
                </Flex>
              )}
            </>
          )}

          {activeTab === "BANK" && <PaymentBankFields formik={formik} />}
        </Flex>
      </Flex>
    </SideSheetDrawer>
  );
};

export default PaymentDrawer;
